import { useEffect, useState, useRef } from "react"
import { treeTypeService } from "../../services/treeTypeService"
import { TreeType } from "../../types"

// ── Preset color palette ──────────────────────────────────────────────────────
const COLORS = [
  "#10b981", "#34d399", "#6ee7b7", "#059669", "#047857",
  "#f59e0b", "#f97316", "#ef4444", "#ec4899", "#8b5cf6",
  "#6366f1", "#3b82f6", "#06b6d4", "#14b8a6", "#84cc16",
  "#a3e635", "#fbbf24", "#fb923c", "#f87171", "#c084fc",
  "#818cf8", "#60a5fa", "#22d3ee", "#2dd4bf", "#a3e635",
  "#6b4f2a", "#7b2d00", "#a0522d", "#9d6b53", "#4a7c59",
  "#2d6a4f", "#1b4332", "#d62828", "#e9c46a", "#7b2d8b",
]

// ── Color Picker ──────────────────────────────────────────────────────────────
function ColorPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (c: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState(value)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-2 bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.16] rounded-xl transition-colors"
      >
        <span
          className="w-5 h-5 rounded-md border border-white/10 flex-shrink-0"
          style={{ backgroundColor: value }}
        />
        <span className="text-neutral-400 text-xs font-mono">{value}</span>
        <svg className="w-3.5 h-3.5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 z-50 bg-[#1a1a1a] border border-white/10 rounded-2xl p-4 shadow-2xl w-64">
          {/* Preset swatches */}
          <p className="text-neutral-600 text-[10px] uppercase tracking-widest mb-2">Presets</p>
          <div className="grid grid-cols-9 gap-1.5 mb-4">
            {COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setCustom(c); setOpen(false) }}
                className="w-6 h-6 rounded-md border-2 transition-all hover:scale-110"
                style={{
                  backgroundColor: c,
                  borderColor: value === c ? "#fff" : "transparent",
                }}
              />
            ))}
          </div>

          {/* Custom hex input */}
          <p className="text-neutral-600 text-[10px] uppercase tracking-widest mb-2">Custom hex</p>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={custom}
              onChange={e => { setCustom(e.target.value); onChange(e.target.value) }}
              className="w-8 h-8 rounded-lg cursor-pointer bg-transparent border-0 p-0"
            />
            <input
              type="text"
              value={custom}
              maxLength={7}
              onChange={e => {
                setCustom(e.target.value)
                if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onChange(e.target.value)
              }}
              className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-white text-xs font-mono outline-none focus:border-emerald-500/50"
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ── Tree Type Row ─────────────────────────────────────────────────────────────
function TreeTypeRow({
  type,
  onSave,
  onDelete,
}: {
  type: TreeType
  onSave: (id: number, payload: { name: string; color_hex: string; is_active: boolean }) => Promise<void>
  onDelete: (id: number) => void
}) {
  const [editing, setEditing]   = useState(false)
  const [name, setName]         = useState(type.name)
  const [color, setColor]       = useState(type.color_hex)
  const [active, setActive]     = useState(type.is_active)
  const [saving, setSaving]     = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    await onSave(type.id, { name: name.trim(), color_hex: color, is_active: active })
    setSaving(false)
    setEditing(false)
  }

  const handleCancel = () => {
    setName(type.name)
    setColor(type.color_hex)
    setActive(type.is_active)
    setEditing(false)
  }

  return (
    <tr className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors group">
      {/* Color dot */}
      <td className="px-5 py-3.5 w-12">
        <span
          className="w-4 h-4 rounded-full block ring-2 ring-white/10"
          style={{ backgroundColor: editing ? color : type.color_hex }}
        />
      </td>

      {/* Name */}
      <td className="px-3 py-3.5">
        {editing ? (
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            className="bg-white/[0.06] border border-white/[0.1] focus:border-emerald-500/50 rounded-lg px-3 py-1.5 text-white text-sm outline-none w-full max-w-xs"
            autoFocus
          />
        ) : (
          <span className="text-white text-sm font-medium">{type.name}</span>
        )}
      </td>

      {/* Color picker (edit mode only) */}
      <td className="px-3 py-3.5">
        {editing ? (
          <ColorPicker value={color} onChange={setColor} />
        ) : (
          <span className="text-neutral-600 text-xs font-mono">{type.color_hex}</span>
        )}
      </td>

      {/* Status */}
      <td className="px-3 py-3.5">
        {editing ? (
          <button
            type="button"
            onClick={() => setActive(v => !v)}
            className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${active ? "bg-emerald-500" : "bg-neutral-700"}`}
          >
            <span
              className={`inline-block w-3.5 h-3.5 rounded-full bg-white shadow transform transition-transform mt-[3px] ${active ? "translate-x-[18px]" : "translate-x-[3px]"}`}
            />
          </button>
        ) : (
          <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium ${
            type.is_active
              ? "bg-emerald-500/10 text-emerald-400"
              : "bg-neutral-800 text-neutral-500"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${type.is_active ? "bg-emerald-400" : "bg-neutral-600"}`} />
            {type.is_active ? "Active" : "Inactive"}
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="px-5 py-3.5 text-right">
        {editing ? (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleCancel}
              className="text-neutral-500 hover:text-neutral-300 text-xs px-3 py-1.5 rounded-lg border border-white/[0.06] hover:border-white/[0.12] transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-black bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setEditing(true)}
              className="text-neutral-500 hover:text-white text-xs px-3 py-1.5 rounded-lg border border-white/[0.06] hover:border-white/[0.12] transition-all"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(type.id)}
              className="text-neutral-600 hover:text-red-400 text-xs px-3 py-1.5 rounded-lg border border-white/[0.06] hover:border-red-500/20 transition-all"
            >
              Delete
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

// ── Add Row ───────────────────────────────────────────────────────────────────
function AddRow({ onAdd }: { onAdd: (name: string, color: string) => Promise<void> }) {
  const [name, setName]   = useState("")
  const [color, setColor] = useState("#10b981")
  const [saving, setSaving] = useState(false)

  const handleAdd = async () => {
    if (!name.trim()) return
    setSaving(true)
    await onAdd(name.trim(), color)
    setName("")
    setColor("#10b981")
    setSaving(false)
  }

  return (
    <tr className="border-b border-white/[0.04] bg-emerald-500/[0.02]">
      <td className="px-5 py-3">
        <span className="w-4 h-4 rounded-full block ring-2 ring-white/10" style={{ backgroundColor: color }} />
      </td>
      <td className="px-3 py-3">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
          placeholder="New tree type name..."
          className="bg-white/[0.06] border border-emerald-500/20 focus:border-emerald-500/50 rounded-lg px-3 py-1.5 text-white text-sm outline-none w-full max-w-xs placeholder:text-neutral-600"
        />
      </td>
      <td className="px-3 py-3">
        <ColorPicker value={color} onChange={setColor} />
      </td>
      <td className="px-3 py-3">
        <span className="text-neutral-600 text-xs">Active by default</span>
      </td>
      <td className="px-5 py-3 text-right">
        <button
          onClick={handleAdd}
          disabled={saving || !name.trim()}
          className="text-black bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-bold px-4 py-1.5 rounded-lg transition-all"
        >
          {saving ? "Adding..." : "+ Add"}
        </button>
      </td>
    </tr>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminTreeTypes() {
  const [types, setTypes]       = useState<TreeType[]>([])
  const [loading, setLoading]   = useState(true)
  const [search, setSearch]     = useState("")
  const [toast, setToast]       = useState<string | null>(null)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const load = async () => {
    try {
      const data = await treeTypeService.getAll()
      setTypes(data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleAdd = async (name: string, color: string) => {
    await treeTypeService.createBulk([{ name, color_hex: color }])
    await load()
    showToast(`"${name}" added successfully.`)
  }

  const handleSave = async (
    id: number,
    payload: { name: string; color_hex: string; is_active: boolean }
  ) => {
    await treeTypeService.update(id, payload)
    await load()
    showToast("Tree type updated.")
  }

  const handleDelete = async (id: number) => {
    const type = types.find(t => t.id === id)
    if (!window.confirm(`Delete "${type?.name}"? This cannot be undone.`)) return
    await treeTypeService.delete(id)
    setTypes(prev => prev.filter(t => t.id !== id))
    showToast(`"${type?.name}" deleted.`)
  }

  const filtered = types.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-5">

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-500 text-black text-sm font-bold px-4 py-3 rounded-xl shadow-xl animate-fade-in">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-neutral-500 text-xs mt-1">{types.length} tree types in the catalogue</p>
        </div>
        <div className="relative">
          <svg className="w-4 h-4 text-neutral-600 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tree types..."
            className="bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.14] focus:border-emerald-500/40 rounded-xl pl-9 pr-4 py-2 text-white text-sm outline-none placeholder:text-neutral-600 w-56 transition-colors"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 gap-3 text-neutral-500">
            <svg className="w-5 h-5 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading tree types...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-neutral-600 tracking-wider uppercase w-12">Color</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-neutral-600 tracking-wider uppercase">Name</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-neutral-600 tracking-wider uppercase">Hex</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-neutral-600 tracking-wider uppercase">Status</th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-neutral-600 tracking-wider uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Add new row always at top */}
                <AddRow onAdd={handleAdd} />

                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-neutral-700 text-sm">
                      {search ? `No tree types matching "${search}"` : "No tree types yet — add one above."}
                    </td>
                  </tr>
                ) : (
                  filtered.map(type => (
                    <TreeTypeRow
                      key={type.id}
                      type={type}
                      onSave={handleSave}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Color legend */}
      {types.length > 0 && (
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
          <p className="text-white text-sm font-bold mb-3">Color Library</p>
          <div className="flex flex-wrap gap-2">
            {types.map(t => (
              <div
                key={t.id}
                className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5"
              >
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.color_hex }} />
                <span className="text-neutral-400 text-xs">{t.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}